import React, { useState, useContext, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import loadable from '@loadable/component';
import 'isomorphic-unfetch';
import mergeWith from 'lodash/mergeWith';
import { Panel } from '@prisjakt/ui/panel';
import { ScrollElement } from 'src/components/scroll';
import {
  getCurrentCampaign,
  mapStateToProps,
  dispatchProps,
  fetchDeals,
} from './utils';
import AdContainer from '../../components/ads';
import FeedbackButton from '../feedback/feedback-button';
import Lazy from '@prisjakt/ui-lazy';
import { PageContent, PageSection } from 'src/components/page';
import RecentlyVisitedProducts from '../../components/recently-visited';
import { readPersonalizationSettings } from '../user/storage/personalization';
import productPageQuery from './query.graphql';
import {
  PRODUCT_ALERT_MODAL_ID,
  PRODUCT_CONTEXT_ID,
  mapOptionsToFilters,
  initialFilterState,
} from './constants';

import { FilterType, PriceType, ProductPageQuery } from '../../types';
import { CurrentCampaign, ProductPageProps } from './types';
import { setSelectedVariant, setSelectedCondition } from './actions/product';
import {
  Variables,
  RouteReducerState,
  RouteReducerActions,
  Loaded,
  Group,
} from './product-page-types';
import ProductPageHeader from './components/product-header';
import ProductTabs from './components/product-tabs';
import ProductDuringPromoBanner from './components/product-promo-banner/product-during-promo-banner';
import ProductBreadCrumb from './components/product-breadcrumb';
import ProductBeforePromoBanner from './components/product-promo-banner/product-before-promo-banner';
import ProductScrollTopButton from './components/product-scroll-top-button';
import ProductCampaignDeals from './components/product-campaign-deals';
import { PriceListFilterContextContainer } from './price-list-filter-context';
import { useEffectOnMount } from '../../hooks/useEffectOnMount';
import ProductNavigation from './components/product-section-navigation';
import MobileTopAd from './components/price-list/ads/mobile-top-ad';
import { getFilterFromLocation } from './components/product-filters/utils';
import { defaultProductState } from './reducer';
import {
  TabContentWrapper,
  ProductPage as StyledProductPage,
  Modal,
} from './styles';

const ProductAlertModalContent = loadable(() =>
  import(
    /* webpackChunkName: "extra" */ '../product-alert/components/product-alert-modal-content'
  )
);

const ProductPage = ({
  activeCampaign,
  data,
  routeSection = 'main',
  campaignBanners,
  currentModal,
  me,
  hideModal,
}: ProductPageProps) => {
  const { t, market } = useContext(AppContext);
  const [isHeaderVisible, setHeaderVisible] = useState<boolean>(true);
  const [campaigns, setCampaignsState] = useState([]);
  const dispatch = useDispatch();
  const { product, recommendations } = data;

  useEffectOnMount(() => {
    // Reset variants and conditions state
    dispatch(setSelectedVariant({ id: 0 }));
    dispatch(setSelectedCondition('new'));
  });

  useEffect(() => {
    if (!IS_BROWSER || !product || !product.id) {
      return;
    }

    fetchDeals(market, product.id).then(
      res =>
        res.hits &&
        res.hits.hits &&
        setCampaignsState(
          res.hits.hits.map((hit: any) => ({
            id: hit._source.campaign.id,
            price: { id: hit._source.priceInfo.priceId.toString() },
          }))
        )
    );
  }, [product && product.id]); // eslint-disable-line

  if (!product) return null;

  product.campaigns = campaigns;

  const privacyPolicyUri = market?.privacySettings?.responsive?.privacyUrl;

  const currentCampaign = getCurrentCampaign(
    activeCampaign,
    product
  ) as CurrentCampaign;

  return (
    <>
      {me && me.analyticsAccess && (
        <FeedbackButton>{t('feedback', 'button.text')}</FeedbackButton>
      )}

      <PageContent withAds>
        <StyledProductPage>
          {currentModal === PRODUCT_ALERT_MODAL_ID && (
            <Modal
              id={PRODUCT_ALERT_MODAL_ID}
              onRequestClose={hideModal}
              width={500}
            >
              <ProductAlertModalContent
                productId={product.id}
                hideModal={hideModal}
                privacyPolicyUri={privacyPolicyUri || undefined}
              />
            </Modal>
          )}
          <ProductDuringPromoBanner
            product={product}
            campaignBanners={campaignBanners}
          />
          <MobileTopAd />

          <ProductBreadCrumb product={product} />
          <ScrollElement name="Top" />
          <PriceListFilterContextContainer>
            <ProductPageHeader
              product={product}
              isHeaderVisible={isHeaderVisible}
              setHeaderVisible={setHeaderVisible}
              currentCampaign={currentCampaign}
            />
            <ProductScrollTopButton />
            <ProductNavigation />
            <TabContentWrapper>
              <ProductTabs
                routeSection={routeSection}
                product={product}
                recommendations={recommendations}
                currentCampaign={currentCampaign}
              />

              <Panel className="post-tabs-ad">
                <AdContainer
                  placement="panorama_target"
                  format="panorama_3_wde"
                  min={861}
                  max={10000}
                  scalable
                  scaleBase=".post-tabs-ad"
                  triggerPoint={100}
                />
              </Panel>

              <ProductCampaignDeals product={product} />

              <section>
                <Lazy offset={100}>
                  <PageSection recessed>
                    <RecentlyVisitedProducts offset={1} background />
                  </PageSection>
                </Lazy>
              </section>
              <ProductBeforePromoBanner campaignBanners={campaignBanners} />
            </TabContentWrapper>
          </PriceListFilterContextContainer>
        </StyledProductPage>
      </PageContent>
    </>
  );
};

ProductPage.graphQL = productPageQuery;

// Group on product ID, ignore section param
ProductPage.group = ({ name, match }: Group) =>
  `${name}_${match && match.params ? match.params.id : 'unknown'}`;

ProductPage.variables = ({
  id,
  offset,
  section,
  marketCode,
  filters,
}: Variables) => {
  const excludeTypes = Object.keys(initialFilterState)
    .filter(option => !initialFilterState[option])
    .map(filter => mapOptionsToFilters[filter])
    .filter(x => x);

  const filterQuery: FilterType[] = Array.isArray(filters)
    ? filters
    : (filters?.split(',') as FilterType[]);

  const expertsEnabled = ['se'].includes(marketCode);
  return {
    id: parseInt(id, 10),
    offset: parseInt(offset, 10) || 0,
    section,
    statisticsTime: section === 'statistics' ? '1970-01-02' : undefined,
    marketCode,
    personalizationExcludeCategories: readPersonalizationSettings().notInterestedIn.categories.map(
      (category: { id: string }) => +category.id
    ),
    recommendationsContextId: PRODUCT_CONTEXT_ID,
    includeSecondary: false,
    excludeTypes,
    variants: null,
    advized: expertsEnabled,
    condition: 'new',
    filter: filterQuery,
  };
};

ProductPage.reducer = (
  state: RouteReducerState,
  action: RouteReducerActions
) => {
  switch (action.type) {
    case 'PRICE_FILTER':
      return {
        ...state,
        options: action.options,
        variants: action.variants,
        condition: action.condition,
      };

    case 'PRODUCT_VARIANTS':
      return {
        ...state,
        variants: action.variants,
        options: action.options,
        condition: action.condition,
      };

    case 'PRODUCT_CONDITION':
      return {
        ...state,
        condition: action.condition,
        options: action.options,
      };
    case 'SSR': {
      const filters = getFilterFromLocation(action.location);

      const currentState = state ?? defaultProductState;

      if (currentState?.filters?.toString() !== filters?.toString()) {
        return { ...currentState, filters };
      }

      return currentState;
    }
    default: {
      const currentState = state ?? defaultProductState;
      return currentState;
    }
  }
};

ProductPage.stateToVariables = (state: RouteReducerState) => {
  if (!state) return {};
  const excludeTypes = state.options
    ? Object.keys(state.options)
        .filter(option => !state.options[option])
        .map<PriceType>(filter => mapOptionsToFilters[filter])
        .filter(x => x)
    : [];

  return {
    ...state,
    excludeTypes,
    includeSecondary: state.options?.showInternational ?? false,
    priceList: true,
    variants: state.variants ?? null,
    condition: state.condition ?? 'new',
    includeShipping: state.options?.showPricesInclShipping ?? false,
  };
};

ProductPage.loadOrder = (
  { section = 'main', marketCode = undefined } = {},
  loaded = {} as Loaded,
  options = { initialRoute: false }
) => {
  const sectionContent = {
    main: {
      priceList: true,
    },
    properties: {
      productProperties: true,
    },
    reviews: {
      userReviews: true,
    },
    statistics: {
      priceHistory: true,
      importantDates: true,
      priceHistoryTable: true,
      priceTrend: true,
      medianPrice: true,
    },
  };

  return [
    {
      main: !loaded.main,
      ...sectionContent[section],
    },
    {
      // Refresh price list for cached pages only
      priceList: options.initialRoute && section === 'main',
      userActions: !loaded.userActions,
      badges: !loaded.badges,
      media: !loaded.media,
      campaign: !loaded.campaign,
      relatedProducts: !loaded.relatedProducts,
      campaignDeals: !loaded.campaignDeals,
      priceHistory: !loaded.priceHistory,
    },
  ];
};

ProductPage.mergeData = (
  oldData: ProductPageQuery,
  newData: ProductPageQuery
) => {
  const data = mergeWith({}, oldData, newData);

  // If price data was refetched always use data from latest fetch
  if (newData?.product?.prices) {
    return {
      ...data,
      product: {
        ...data.product,
        prices: newData.product.prices,
        variantGroups: newData.product.variantGroups,
      },
    };
  }

  return data;
};

export default connect(mapStateToProps, dispatchProps)(ProductPage);
